
import React, { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { FileX } from "lucide-react";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import CountryFlag from "@/components/country/CountryFlag";

interface DocumentPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedDocument: any;
}

export function DocumentPreviewDialog({
  open,
  onOpenChange,
  selectedDocument,
}: DocumentPreviewDialogProps) {
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [loadingDocument, setLoadingDocument] = useState(false);
  const [documentError, setDocumentError] = useState<string | null>(null);
  const [isImage, setIsImage] = useState(false);

  const handleViewDocument = async (document: any) => {
    setLoadingDocument(true);
    setDocumentError(null);
    setIsImage(false);
    
    try {
      const { data, error } = await supabase.storage
        .from("identity_docs")
        .createSignedUrl(document.file_url, 60);
      if (error) throw error;
      setDocumentUrl(data.signedUrl);
    } catch (error) {
      console.error("Error fetching document URL:", error);
      setDocumentError(
        "Failed to load document preview. Please try again later."
      );
    } finally {
      setLoadingDocument(false);
    }
  };

  // Detect if the URL points to an image (using same logic as admin component)
  useEffect(() => {
    if (!documentUrl) {
      setIsImage(false);
      return;
    }

    const checkExtension = (url: string) => {
      const extension = url.split(/[?#]/)[0].split('.').pop()?.toLowerCase();
      const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];
      return imageExtensions.includes(extension || '');
    };

    if (checkExtension(documentUrl) || checkExtension(selectedDocument?.file_url || '')) {
      setIsImage(true);
      return;
    }

    const img = new Image();
    img.onload = () => setIsImage(true);
    img.onerror = () => setIsImage(false);
    img.src = documentUrl;

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [documentUrl, selectedDocument?.file_url]);

  useEffect(() => {
    if (open && selectedDocument) {
      handleViewDocument(selectedDocument);
    }
  }, [open, selectedDocument]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Document Preview</DialogTitle>
          {selectedDocument && (
            <>
              <DialogDescription>
                Document preview and verification details
              </DialogDescription>
              <div className="flex flex-col space-y-2 pt-2">
                <div className="flex items-center gap-2">
                  <CountryFlag
                    countryCode={
                      selectedDocument.id_document_types?.country_code ||
                      selectedDocument.verification_country
                    }
                    className="h-4 w-5"
                  />
                  <span className="text-sm font-medium">
                    {selectedDocument.id_document_types?.document_name ||
                      "Document"}{" "}
                    – {selectedDocument.verification_country}
                  </span>
                  <Badge
                    variant={
                      selectedDocument.verification_status === "verified"
                        ? "success"
                        : selectedDocument.verification_status === "rejected"
                        ? "destructive"
                        : "outline"
                    }
                    className="ml-1"
                  >
                    {selectedDocument.verification_status
                      .charAt(0)
                      .toUpperCase() +
                      selectedDocument.verification_status.slice(1)}
                  </Badge>
                </div>
                <div className="text-xs text-muted-foreground">
                  <span className="mr-3">
                    Document #: {selectedDocument.document_number}
                  </span>
                  {selectedDocument.issue_date && (
                    <span className="mr-3">
                      Issued:{" "}
                      {format(
                        new Date(selectedDocument.issue_date),
                        "MMM d, yyyy"
                      )}
                    </span>
                  )}
                  {selectedDocument.expiry_date && (
                    <span>
                      Expires:{" "}
                      {format(
                        new Date(selectedDocument.expiry_date),
                        "MMM d, yyyy"
                      )}
                    </span>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogHeader>
        <div className="flex-grow w-full h-0 overflow-auto">
          {loadingDocument && (
            <div className="flex items-center justify-center h-full">
              <p className="text-muted-foreground">Loading document...</p>
            </div>
          )}
          {!loadingDocument &&
            documentUrl &&
            !documentError &&
            (isImage ? (
              <div className="flex items-center justify-center h-full">
                <img
                  src={documentUrl}
                  alt="Document preview"
                  className="max-w-full max-h-full object-contain"
                  onError={() =>
                    setDocumentError("Failed to load image preview.")
                  }
                />
              </div>
            ) : (
              <iframe
                src={documentUrl}
                width="100%"
                height="100%"
                className="w-full h-full border-0"
                title="Document preview"
                onError={() =>
                  setDocumentError("Failed to load PDF preview.")
                }
              >
                <p>
                  Your browser does not support embedded PDFs. <br />
                  <a
                    href={documentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    Download the document.
                  </a>
                </p>
              </iframe>
            ))}
          {!loadingDocument && documentError && (
            <div className="flex flex-col items-center justify-center h-full gap-4">
              <div className="rounded-full bg-red-100 p-3">
                <FileX className="h-8 w-8 text-red-500" />
              </div>
              <p className="text-destructive font-medium">{documentError}</p>
              {documentUrl && (
                <a
                  href={documentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  Download the document instead
                </a>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
